import {createPortal} from 'react-dom';
import React, { useEffect, useId, useRef, useState } from "react";
import {
  X,
  ArrowRight,
  PackageOpen,
  RotateCw,
  AlertCircle,
  ChevronDown,
} from "lucide-react";
import { Link } from "react-router-dom";
export function Button({ children, variant = "", className = "", ...props }) {
  return (
    <button
      className={`tb-button ${variant ? `tb-button--${variant}` : ""} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}
export function ActionLink({ children, to, variant = "", ...props }) {
  return (
    <Link
      className={`tb-button ${variant ? `tb-button--${variant}` : ""}`}
      to={to}
      {...props}
    >
      {children}
      <ArrowRight size={17} />
    </Link>
  );
}
export function Field({ label, error, hint, children, className = "" }) {
  const id = useId();
  return (
    <label className={`tb-field ${className}`}>
      <span>{label}</span>
      {React.isValidElement(children)
        ? React.cloneElement(children, {
            "aria-describedby": [children.props['aria-describedby'], (error || hint) && id].filter(Boolean).join(' ') || undefined,
            "aria-invalid": error ? true : children.props['aria-invalid'],
          })
        : children}
      {(error || hint) && (
        <small id={id} className={error ? "tb-field-error" : ""}>
          {error || hint}
        </small>
      )}
    </label>
  );
}
export function Empty({
  title = "Nothing here yet",
  description,
  action,
  icon: Icon = PackageOpen,
}) {
  return (
    <div className="tb-empty">
      <Icon size={32} strokeWidth={1.3} />
      <h2>{title}</h2>
      {description && <p>{description}</p>}
      {action}
    </div>
  );
}
export function ErrorState({ error, retry }) {
  return (
    <div className="tb-error" role="alert">
      <AlertCircle size={20} />
      <div>
        <strong>Let’s try that again.</strong>
        <p>{error?.message || error || "We couldn’t load this page."}</p>
      </div>
      {retry && (
        <Button variant="quiet" onClick={retry}>
          <RotateCw size={16} />
          Retry
        </Button>
      )}
    </div>
  );
}
export function Skeleton({ count = 4, className = "" }) {
  return (
    <div
      className={`tb-skeleton-grid ${className}`}
      aria-label="Loading"
      aria-busy="true"
    >
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="tb-skeleton">
          <div />
          <span />
          <span />
        </div>
      ))}
    </div>
  );
}
export function Modal({
  title,
  children,
  onClose,
  className = "",
  wide = false,
}) {
  const ref = useRef(null);
  const titleId = useId();
  useEffect(() => {
    const previous = document.activeElement;
    const dialog = ref.current;
    dialog.showModal();
    dialog.focus({ preventScroll: true });
    return () => {
      previous?.setAttribute?.('data-dialog-restoring-focus','true');
      dialog.close();
      if(previous?.isConnected)previous.focus?.({preventScroll:true});
      queueMicrotask(()=>previous?.removeAttribute?.('data-dialog-restoring-focus'));
    };
  }, []);
  return createPortal(
    <dialog
      ref={ref}
      className={`tb-dialog ${wide ? "tb-dialog--wide" : ""} ${className}`}
      aria-labelledby={titleId}
      tabIndex={-1}
      onKeyDown={(e) => {
        if(e.key!=='Tab')return;
        const controls=[...e.currentTarget.querySelectorAll('a[href],button,input,select,textarea,[tabindex]')]
          .filter(el=>el.tabIndex>=0&&!el.disabled&&el.getClientRects().length);
        const first=controls[0],last=controls[controls.length-1];
        if(!first){e.preventDefault();e.currentTarget.focus();}
        else if(e.shiftKey&&(document.activeElement===first||document.activeElement===e.currentTarget)){
          e.preventDefault();last.focus();
        } else if(!e.shiftKey&&(document.activeElement===last||document.activeElement===e.currentTarget)){
          e.preventDefault();first.focus();
        }
      }}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget) {
          const b = e.currentTarget.getBoundingClientRect();
          if (
            e.clientX < b.left ||
            e.clientX > b.right ||
            e.clientY < b.top ||
            e.clientY > b.bottom
          )
            onClose();
        }
      }}
    >
      <div className="tb-dialog-head">
        <h2 id={titleId}>{title}</h2>
        <button type="button" className="tb-icon-button" onClick={e=>{e.preventDefault();e.stopPropagation();onClose();}} aria-label="Close">
          <X size={21} />
        </button>
      </div>
      <div className="tb-dialog-content">{children}</div>
    </dialog>,document.body
  );
}
export function Status({ children, value = "" }) {
  return (
    <span className={`tb-status tb-status--${value}`}>
      {children || value.replace(/_/g, " ")}
    </span>
  );
}
export function PageHeader({ eyebrow, title, description, actions }) {
  return (
    <header className="tb-page-head">
      <div>
        {eyebrow && <p className="tb-kicker">{eyebrow}</p>}
        <h1>{title}</h1>
        {description && <p className="tb-page-description">{description}</p>}
      </div>
      {actions && <div className="tb-head-actions">{actions}</div>}
    </header>
  );
}
export function SectionHeader({ kicker, title, to, link = "Explore" }) {
  return (
    <header className="tb-section-head">
      <div>
        {kicker && <p className="tb-kicker">{kicker}</p>}
        <h2>{title}</h2>
      </div>
      {to && (
        <Link to={to}>
          {link}
          <ArrowRight size={18} />
        </Link>
      )}
    </header>
  );
}
export function useResource(load, dependencies = []) {
  const [state, setState] = useState({
    data: null,
    loading: true,
    error: null,
  });
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    setState((s) => ({ ...s, loading: true, error: null }));
    let pending=false, failed=false;
    const run=()=>{
      if(pending||!alive)return;
      pending=true;
      Promise.resolve().then(()=>load(controller.signal)).then(data=>{
        failed=false;if(alive)setState({data,loading:false,error:null});
      }).catch(error=>{
        if(alive&&error.name!=="AbortError"){
          failed=[500,502,503,504].includes(error.status);
          setState(s=>({...s,loading:false,error}));
        }
      }).finally(()=>{pending=false;});
    };
    const recover=()=>{if(failed&&!document.hidden&&navigator.onLine)run();};
    run();
    // Recover a failed initial read without requiring a page refresh.
    const timer=setInterval(recover,15000);
    window.addEventListener('online',recover);window.addEventListener('focus',recover);document.addEventListener('visibilitychange',recover);
    return()=>{alive=false;controller.abort();clearInterval(timer);window.removeEventListener('online',recover);window.removeEventListener('focus',recover);document.removeEventListener('visibilitychange',recover);};
  }, [...dependencies, version]); // eslint-disable-line react-hooks/exhaustive-deps
  return {
    ...state,
    reload: () => setVersion((v) => v + 1),
    setData: (data) => setState((s) => ({ ...s, data })),
  };
}
export function Accordion({ title, children, open = false }) {
  return (
    <details className="tb-accordion" open={open || undefined}>
      <summary>
        {title}
        <ChevronDown size={18} />
      </summary>
      <div>{children}</div>
    </details>
  );
}
export function Notice({ children }) {
  return (
    <p className="tb-notice" role="status">
      {children}
    </p>
  );
}
